import { getFlag } from '@nx/launch-darkly-service';
import { isNullish } from '@nx/stdlib';
import { deepmerge } from 'deepmerge-ts';
import { useCallback } from 'react';

import { useDispatch, useSelector } from '../../context';
import { useActiveProject } from '../../hooks/use-app-context';
import { useCapability } from '../../hooks/use-capability';
import { useSharedStorageApi } from '../../hooks/use-shared-storage-api';
import { selectSettings } from '../../selectors';
import { updateAppSetting } from './actions';
import { useGetSettingsQueryInternal } from './settings.api';
import type { SettingsDocument, UpdateSettingsApiArg } from './settings.api.types';
import type { Settings } from './types';

type UpdateAppSettingFn = (arg: Omit<UpdateSettingsApiArg, 'id' | 'projectId'>) => Promise<void>;

const deepMergeSettings = (
  sharedStorageSettings: Omit<SettingsDocument, 'createdAt' | 'updatedAt'> | undefined,
  localStorageSettings: Settings,
) => {
  return deepmerge(localStorageSettings, sharedStorageSettings);
};

const useAppSettingsStorageApiEnabled = () => {
  const [storageApiEnabled] = useCapability('framework:storage-api');
  const ldFlagEnabled = getFlag('upxAppSettingsStorageApi', false) ?? false;
  const featureFlagEnabled = useSharedStorageApi('nx:app-settings-shared-storage');
  return storageApiEnabled && (featureFlagEnabled || ldFlagEnabled);
};

export const useMergedAppSettings = () => {
  const appSettingsStorageApiEnabled = useAppSettingsStorageApiEnabled();
  const activeProject = useActiveProject(false);
  const skip = isNullish(activeProject) || !appSettingsStorageApiEnabled;
  const { data: sharedStorageSettings } = useGetSettingsQueryInternal({ projectId: activeProject?.id ?? '' }, { skip });
  const localStorageSettings = useSelector(selectSettings);
  return deepMergeSettings(sharedStorageSettings, localStorageSettings);
};

export const useUpdateAppSetting = () => {
  const dispatch = useDispatch();

  const useUpdateAppSettingInternal: UpdateAppSettingFn = useCallback(
    async ({ scope, key, value }) => {
      await dispatch(
        updateAppSetting({
          scope,
          key,
          value,
        }),
      );
    },
    [dispatch],
  );

  return [useUpdateAppSettingInternal] as const;
};
